/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * Base Adapter Interface
 * All database adapters must implement this interface
 */
export class BaseAdapter {
  /**
   * Initialize the database adapter
   * @abstract
   * @returns {Promise<void>}
   */
  async initialize() {
    throw new Error('initialize() must be implemented by adapter');
  }

  /**
   * Execute a SELECT query
   * @abstract
   * @param {string} sql - SQL query string
   * @param {Array} params - Query parameters
   * @returns {Promise<{values: Array}>} Query results
   */
  async query(sql, params = []) {
    throw new Error('query() must be implemented by adapter');
  }

  /**
   * Execute an INSERT, UPDATE, or DELETE command
   * @abstract
   * @param {string} sql - SQL command string
   * @param {Array} params - Command parameters
   * @returns {Promise<{changes: {lastId: number}}>} Execution result
   */
  async run(sql, params = []) {
    throw new Error('run() must be implemented by adapter');
  }

  /**
   * Execute raw SQL (for schema creation)
   * @abstract
   * @param {string} sql - SQL statements
   * @returns {Promise<void>}
   */
  async execute(sql) {
    throw new Error('execute() must be implemented by adapter');
  }

  /**
   * Close the database connection
   * @abstract
   * @returns {Promise<void>}
   */
  async close() {
    throw new Error('close() must be implemented by adapter');
  }

  /**
   * Drop all tables and reset the database schema
   * @abstract
   * @returns {Promise<void>}
   */
  async dropAllTables() {
    throw new Error('dropAllTables() must be implemented by adapter');
  }

  /**
   * Get the adapter name
   * @abstract
   * @returns {string} Adapter name
   */
  getName() {
    throw new Error('getName() must be implemented by adapter');
  }
}
